import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    FormControlLabel,
    Switch,
    Tabs,
    Tab,
    List,
    ListItem,
    Checkbox,
    ListItemText,
    Typography,
    Divider,
    InputAdornment
} from '@mui/material';
import api from '../../services/api';
import { formatarMoeda } from '../../utils/formatadores';

// Helper to manually parse currency input to cents
const parseCurrencyToCents = (value) => {
    if (!value) return 0;
    // Remove "R$", spaces, dots, and replace comma with dot
    const clean = value.replace(/[^\d,]/g, '').replace(',', '.');
    return Math.round(parseFloat(clean) * 100);
};

// Helper to format cents to UI string
const formatCentsToCurrency = (cents) => {
    if (cents === undefined || cents === null) return '';
    return (cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export default function CadastroPlano({ open, onClose, planoId, onSuccess }) {
    const [tabIndex, setTabIndex] = useState(0);
    const [loading, setLoading] = useState(false);

    // Dados do Formulário
    const [formData, setFormData] = useState({
        st_nome: '',
        st_descricao: '',
        nu_valor_mensal_centavos: 0,
        nu_valor_adesao_centavos: 0,
        nu_limite_dependentes: 0,
        bo_permite_dependente: true,
        nu_carencia_dias_padrao: 0,
        bo_ativo: true,
        procedimentos: [] // Array de IDs ou Objetos
    });

    // Inputs visuais (strings com mascaras)
    const [valorMensalStr, setValorMensalStr] = useState('');
    const [valorAdesaoStr, setValorAdesaoStr] = useState('');

    // Lista completa de procedimentos disponíveis no sistema
    const [procedimentosDisponiveis, setProcedimentosDisponiveis] = useState([]);

    useEffect(() => {
        carregarProcedimentos();
        if (planoId) {
            carregarPlano(planoId);
        }
    }, [planoId]);

    const carregarProcedimentos = async () => {
        try {
            const res = await api.get('/procedimento?ativo=true');
            setProcedimentosDisponiveis(res.data.dados || []);
        } catch (err) {
            console.error(err);
        }
    };

    const carregarPlano = async (id) => {
        setLoading(true);
        try {
            const res = await api.get(`/plano/${id}`);
            const dados = res.data.dados;

            setFormData({
                ...dados,
                procedimentos: dados.procedimentos.map(p => ({
                    id_procedimento: p.id_procedimento,
                    nu_cobertura_percentual: p.PlanoProcedimento.nu_cobertura_percentual,
                    nu_valor_coparticipacao_centavos: p.PlanoProcedimento.nu_valor_coparticipacao_centavos,
                    bo_coberto: p.PlanoProcedimento.bo_coberto
                }))
            });

            setValorMensalStr(formatCentsToCurrency(dados.nu_valor_mensal_centavos));
            setValorAdesaoStr(formatCentsToCurrency(dados.nu_valor_adesao_centavos));

        } catch (err) {
            console.error("Erro ao carregar plano", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        const payload = {
            ...formData,
            nu_valor_mensal_centavos: parseCurrencyToCents(valorMensalStr),
            nu_valor_adesao_centavos: parseCurrencyToCents(valorAdesaoStr)
        };

        try {
            if (planoId) {
                await api.put(`/plano/${planoId}`, payload);
                onSuccess("Plano atualizado com sucesso!");
            } else {
                await api.post('/plano', payload);
                onSuccess("Plano criado com sucesso!");
            }
        } catch (err) {
            console.error(err);
            alert("Erro ao salvar plano.");
        }
    };

    // Toggle procedure inclusion
    const handleToggleProcedimento = (procId) => {
        setFormData(prev => {
            const exists = prev.procedimentos.find(p => p.id_procedimento === procId);
            if (exists) {
                return { ...prev, procedimentos: prev.procedimentos.filter(p => p.id_procedimento !== procId) };
            } else {
                return {
                    ...prev,
                    procedimentos: [...prev.procedimentos, {
                        id_procedimento: procId,
                        nu_cobertura_percentual: 100,
                        nu_valor_coparticipacao_centavos: 0,
                        bo_coberto: true
                    }]
                };
            }
        });
    };

    // Update procedure specific field (copay, coverage)
    const handleUpdateProcedimentoField = (procId, field, value) => {
        setFormData(prev => ({
            ...prev,
            procedimentos: prev.procedimentos.map(p => {
                if (p.id_procedimento !== procId) return p;
                return { ...p, [field]: value };
            })
        }));
    };

    const getProcedimentoData = (procId) => formData.procedimentos.find(p => p.id_procedimento === procId);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{planoId ? "Editar Plano" : "Novo Plano"}</DialogTitle>
            <DialogContent dividers>
                <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v)} sx={{ mb: 2 }}>
                    <Tab label="Dados Gerais" />
                    <Tab label="Procedimentos e Coberturas" />
                </Tabs>

                {tabIndex === 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Nome do Plano"
                            fullWidth
                            value={formData.st_nome}
                            onChange={e => setFormData({ ...formData, st_nome: e.target.value })}
                        />
                        <TextField
                            label="Descrição"
                            fullWidth
                            multiline
                            rows={3}
                            value={formData.st_descricao}
                            onChange={e => setFormData({ ...formData, st_descricao: e.target.value })}
                        />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="Valor Mensal (R$)"
                                value={valorMensalStr}
                                onChange={e => setValorMensalStr(e.target.value)}
                                fullWidth
                            />
                            <TextField
                                label="Valor Adesão (R$)"
                                value={valorAdesaoStr}
                                onChange={e => setValorAdesaoStr(e.target.value)}
                                fullWidth
                            />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="Limite Dependentes"
                                type="number"
                                value={formData.nu_limite_dependentes}
                                onChange={e => setFormData({ ...formData, nu_limite_dependentes: parseInt(e.target.value) })}
                                fullWidth
                            />
                            <TextField
                                label="Carência (Dias)"
                                type="number"
                                value={formData.nu_carencia_dias_padrao}
                                onChange={e => setFormData({ ...formData, nu_carencia_dias_padrao: parseInt(e.target.value) })}
                                fullWidth
                            />
                        </Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.bo_permite_dependente}
                                    onChange={e => setFormData({ ...formData, bo_permite_dependente: e.target.checked })}
                                />
                            }
                            label="Permite Dependentes?"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.bo_ativo}
                                    onChange={e => setFormData({ ...formData, bo_ativo: e.target.checked })}
                                />
                            }
                            label="Ativo"
                        />
                    </Box>
                )}

                {tabIndex === 1 && (
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                            Selecione os procedimentos cobertos por este plano e defina a coparticipação.
                        </Typography>
                        <List sx={{ width: '100%', bgcolor: 'background.paper', maxHeight: 400, overflow: 'auto' }}>
                            {procedimentosDisponiveis.map((proc) => {
                                const isSelected = !!getProcedimentoData(proc.id_procedimento);
                                const currentData = getProcedimentoData(proc.id_procedimento) || {};

                                return (
                                    <React.Fragment key={proc.id_procedimento}>
                                        <ListItem alignItems="flex-start" sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Checkbox
                                                    checked={isSelected}
                                                    onChange={() => handleToggleProcedimento(proc.id_procedimento)}
                                                />
                                                <ListItemText
                                                    primary={proc.st_nome}
                                                    secondary={`Código: ${proc.st_codigo || 'N/A'}`}
                                                />
                                            </Box>

                                            {isSelected && (
                                                <Box sx={{ display: 'flex', gap: 2, ml: 6, mb: 1 }}>
                                                    <TextField
                                                        label="% Cobertura"
                                                        size="small"
                                                        type="number"
                                                        value={currentData.nu_cobertura_percentual || 0}
                                                        onChange={(e) => handleUpdateProcedimentoField(proc.id_procedimento, 'nu_cobertura_percentual', parseInt(e.target.value))}
                                                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                                        sx={{ width: 120 }}
                                                    />
                                                    <TextField
                                                        label="Coparticipação (Centavos)"
                                                        size="small"
                                                        type="number"
                                                        value={currentData.nu_valor_coparticipacao_centavos || 0}
                                                        onChange={(e) => handleUpdateProcedimentoField(proc.id_procedimento, 'nu_valor_coparticipacao_centavos', parseInt(e.target.value))}
                                                        helperText="Ex: 1000 = R$ 10,00"
                                                    />
                                                </Box>
                                            )}
                                        </ListItem>
                                        <Divider component="li" />
                                    </React.Fragment>
                                );
                            })}
                            {procedimentosDisponiveis.length === 0 && (
                                <Box p={2}><Typography>Nenhum procedimento cadastrado no sistema.</Typography></Box>
                            )}
                        </List>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button onClick={handleSave} variant="contained" color="primary">Salvar</Button>
            </DialogActions>
        </Dialog>
    );
}
